import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Banner, Button, Card, COPY, EmptyState, Field, Input, Select } from "@magarac/ui";
import { membersListSchema } from "@magarac/shared/schemas/member";
import { resourceListSchema, resourceSchema } from "@magarac/shared/schemas/resource";
import { HelpIcon } from "../../../components/HelpIcon";
import { api } from "../../../lib/api";
import { conflictReasons, messageFor } from "../../../lib/errors";
import { useTenant } from "../../../lib/tenant";

export const Route = createFileRoute("/_app/schedule/")({
  component: SchedulePeoplePage,
});

/**
 * Who is on the schedule (resources). Separate from logins: a team member can be bookable
 * without an account, and an office admin can have an account without being bookable.
 * Screen key: schedule.list
 */
function SchedulePeoplePage() {
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const resources = useQuery({
    queryKey: ["resources", active?.id],
    queryFn: () => api.get("/resources", resourceListSchema, { query: { includeInactive: true } }),
    enabled: Boolean(active),
  });
  const members = useQuery({
    queryKey: ["members", active?.id],
    queryFn: () => api.get("/members", membersListSchema),
    enabled: Boolean(active),
  });
  const [name, setName] = useState("");
  const [memberUserId, setMemberUserId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["resources", active?.id] });

  const create = useMutation({
    mutationFn: () =>
      api.post(
        "/resources",
        { name, memberUserId: memberUserId || null },
        z.object({ resource: resourceSchema }),
      ),
    onSuccess: async () => {
      setName("");
      setMemberUserId("");
      setError(null);
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  // Taking someone off the schedule with visits coming up asks first (the server says how
  // many); confirming moves those visits to Unassigned.
  const [confirmOff, setConfirmOff] = useState<{ id: string; reasons: string[] } | null>(null);
  const toggle = useMutation({
    mutationFn: (r: { id: string; active: boolean; force?: boolean }) =>
      api.patch(
        `/resources/${r.id}`,
        { active: !r.active, ...(r.force ? { force: true } : {}) },
        z.object({ resource: resourceSchema }),
      ),
    onSuccess: async () => {
      setConfirmOff(null);
      setError(null);
      await refresh();
    },
    onError: (err, vars) => {
      const reasons = conflictReasons(err);
      if (reasons && vars.active && !vars.force) setConfirmOff({ id: vars.id, reasons });
      else setError(messageFor(err));
    },
  });

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Who is on the schedule</h1>
        <HelpIcon screen="schedule.list" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <Card title="Add a person to the schedule" className="mb-6">
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto] md:items-end">
          <Field id="res-name" label="Name">
            <Input id="res-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field
            id="res-member"
            label="Their login (optional)"
            hint="Connect a login so they see their own day on their phone."
          >
            <Select
              id="res-member"
              value={memberUserId}
              onChange={(e) => setMemberUserId(e.target.value)}
            >
              <option value="">No login</option>
              {members.data?.members.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name ?? m.email}
                </option>
              ))}
            </Select>
          </Field>
          <div className="mb-4">
            <Button onClick={() => create.mutate()} busy={create.isPending} disabled={!name}>
              Add
            </Button>
          </div>
        </div>
      </Card>
      <Card
        title={`${COPY.teamMembers[0]!.toUpperCase()}${COPY.teamMembers.slice(1)} on the schedule`}
      >
        {resources.data && resources.data.resources.length === 0 ? (
          <EmptyState
            title="Nobody on the schedule yet"
            body="Add at least one person so visits can be booked."
          />
        ) : (
          <ul>
            {resources.data?.resources.map((r) => (
              <li
                key={r.id}
                className={`flex flex-wrap items-center justify-between gap-2 border-b border-line py-3 last:border-0 ${r.active ? "" : "opacity-60"}`}
              >
                <div>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted">
                    {r.memberUserId ? "Has a login" : "No login"}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link to="/schedule/$id" params={{ id: r.id }}>
                    <Button variant="secondary">Hours and time off</Button>
                  </Link>
                  <Button
                    variant="ghost"
                    busy={toggle.isPending && toggle.variables?.id === r.id}
                    onClick={() => toggle.mutate(r)}
                  >
                    {r.active ? "Remove from schedule" : "Put back on schedule"}
                  </Button>
                </div>
                {confirmOff?.id === r.id ? (
                  <div className="mt-2 w-full rounded-md border border-warn bg-canvas p-3 text-sm">
                    {confirmOff.reasons.map((reason) => (
                      <p key={reason} className="mb-2">
                        {reason}
                      </p>
                    ))}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="danger"
                        busy={toggle.isPending}
                        onClick={() => toggle.mutate({ ...r, force: true })}
                      >
                        Remove and move the visits
                      </Button>
                      <Button variant="secondary" onClick={() => setConfirmOff(null)}>
                        Keep on schedule
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
